import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { assignTrack, getPerson, setPersonExempt, type PersonDetail } from '../lib/api';
import { useTracks } from '../lib/data';
import { formatDuration } from '../lib/format-duration';
import { AKHARA_BG, AKHARA_TEXT, MITTI_BG, MITTI_TEXT, niceMax, relativeAgo, weeklyBars } from '../lib/people-time';
import SiteHeader from './SiteHeader';
import { Legend, PausedChip, Tile, TimeBar, TypeChip } from './AdminRoster';

// Person page: how much, when, where on the path, quizzes, Mittis. PersonView is presentational.

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function WeeklyChart({ days }: { days: PersonDetail['days'] }) {
  const bars = weeklyBars(days);
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(...bars.map((b) => b.akharaMin + b.mittiMin), 0));
  const H = 140;
  const showNote = bars.some((b) => b.hasPrior);
  const shown = active != null ? bars[active] : null;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">Last 12 weeks</h3>
        <Legend />
      </div>
      <div className="mt-3 flex gap-2">
        <div className="relative w-9 shrink-0 text-right text-[10px] tabular-nums text-ink-400" style={{ height: H }}>
          {[1, 0.5, 0].map((f) => (
            <span key={f} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - f) * 100}%` }}>
              {f === 0 ? '0' : formatDuration(max * f)}
            </span>
          ))}
        </div>
        <div className="relative flex-1" style={{ height: H }}>
          {[0, 0.5, 1].map((f) => (
            <div
              key={f}
              aria-hidden="true"
              className="absolute left-0 right-0 border-t border-rule dark:border-clay-rule"
              style={{ top: `${(1 - f) * 100}%` }}
            />
          ))}
          <div className="relative flex h-full items-end gap-1.5">
            {bars.map((b, i) => {
              const total = b.akharaMin + b.mittiMin;
              const label = `${fmtDay(b.start)} to ${fmtDay(b.end)}: Akhara ${formatDuration(b.akharaMin)}, Mitti ${formatDuration(b.mittiMin)}`;
              return (
                <button
                  key={b.start}
                  type="button"
                  aria-label={label}
                  title={label}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  className="flex h-full flex-1 flex-col justify-end rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-700 dark:focus-visible:ring-paper-200"
                >
                  <div className="flex w-full flex-col justify-end" style={{ height: `${(total / max) * 100}%` }}>
                    <div className={`${MITTI_BG} w-full`} style={{ height: total ? `${(b.mittiMin / total) * 100}%` : 0 }} />
                    <div
                      className={`${AKHARA_BG} w-full ${b.hasPrior ? 'opacity-70' : ''}`}
                      style={{ height: total ? `${(b.akharaMin / total) * 100}%` : 0 }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-1 flex justify-between pl-11 text-[11px] text-ink-400">
        <span>{bars[0] ? fmtDay(bars[0].start) : ''}</span>
        <span>Week ending {bars.length ? fmtDay(bars[bars.length - 1].end) : ''}</span>
      </div>
      <p className="mt-1 min-h-[1.25rem] text-xs tabular-nums text-ink-600 dark:text-paper-300" aria-live="polite">
        {shown
          ? `${fmtDay(shown.start)} to ${fmtDay(shown.end)}: Akhara ${formatDuration(shown.akharaMin)}, Mitti ${formatDuration(shown.mittiMin)}`
          : 'Hover or focus a bar for exact values.'}
      </p>
      {showNote && (
        <p className="mt-1 text-xs italic text-ink-400">
          Akhara time before 15 Oct 2026 is estimated from page visits, capped at 30 minutes each.
        </p>
      )}
    </div>
  );
}

// Candidates only: the auto-pause switch, what pausing does, and how to bring someone back.
function AutoPause({
  person: p,
  inactivityDays,
  onExempt,
}: {
  person: PersonDetail['person'];
  inactivityDays?: number;
  onExempt?: (exempt: boolean) => void;
}) {
  const exempt = p.inactivityExempt === true;
  const days = inactivityDays ?? 14;
  return (
    <section className="mt-4 rounded-xl border border-rule bg-paper-0 p-3 dark:border-clay-rule dark:bg-clay-surface">
      {p.paused && (
        <p className="text-sm font-medium text-ink-800 dark:text-paper-100">
          Paused on {fmtDate(p.paused.at)} ({p.paused.days ?? days} days inactive)
        </p>
      )}
      <label className="mt-1 flex items-center gap-2 text-sm text-ink-800 dark:text-paper-100">
        <input
          type="checkbox"
          checked={!exempt}
          disabled={!onExempt}
          onChange={(e) => onExempt?.(!e.target.checked)}
        />
        Auto-pause after {days} days inactive
      </label>
      <p className="mt-1 text-xs text-ink-400">
        Pausing removes their Akhara sign-in and their Mitti, and keeps their record, progress and time. Un-revoke their
        invite on the Invites tab to bring them back.
      </p>
    </section>
  );
}

export interface CatalogTrack {
  id: string;
  title: string;
  internal?: boolean;
}

export interface AssignResult {
  error: string | null;
  /** How many were newly given (the rest were already theirs). */
  granted: number;
  /** True when a domain-invite candidate was given their own email invite. */
  viaDomain: boolean;
}

const ASSIGN_ERRORS: Record<string, string> = {
  invite_revoked: 'Their invite is revoked, which is also what the auto-pause does. Un-revoke it on the Invites tab, then assign.',
  invite_expired: 'Their invite has expired. Give it a later expiry on the Invites tab, then assign.',
  no_invite: 'They have no invite to add this to. Create one for them on the Invites tab first.',
  track_not_grantable: 'That kushti is internal (teammates only) or not published.',
  too_many_grants: 'They already have the maximum number of kushtis.',
};

// Assign kushtis to this person. A teammate gets a progress entry; a candidate gets the kushti added to
// their invite, so internal (teammate-only) kushtis are not offered to candidates.
function AssignKushtis({
  data,
  catalog,
  catalogLoading,
  onAssign,
}: {
  data: PersonDetail;
  catalog: CatalogTrack[];
  catalogLoading?: boolean;
  onAssign: (trackIds: string[]) => Promise<AssignResult>;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const isCandidate = data.person.type === 'candidate';
  const have = new Set(data.assignedTrackIds ?? data.tracks.map((t) => t.trackId));
  const options = catalog.filter((t) => t.id !== 'track_welcome' && !have.has(t.id) && !(isCandidate && t.internal));
  const hiddenInternal = isCandidate ? catalog.filter((t) => t.internal && !have.has(t.id)).length : 0;

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    // Only what is still offered: the live list can change under an open page.
    const send = picked.filter((id) => options.some((o) => o.id === id));
    const r = await onAssign(send);
    setBusy(false);
    setPicked([]);
    const detach = r.viaDomain
      ? 'They now have their own invite, so later changes to their domain invite (revoking it, new kushtis, a new expiry) no longer reach them.'
      : '';
    if (r.error) return setMsg({ ok: false, text: [r.error, r.granted > 0 ? `${r.granted} assigned before that.` : '', detach].filter(Boolean).join(' ') });
    const already = send.length - r.granted;
    const parts = [
      r.granted > 0 ? `Assigned ${r.granted} kushti${r.granted === 1 ? '' : 's'}.` : '',
      already > 0 ? `${already} ${already === 1 ? 'was' : 'were'} already theirs.` : '',
      detach,
    ];
    setMsg({ ok: true, text: parts.filter(Boolean).join(' ') });
  };

  return (
    <section className="mt-5">
      <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">Assign kushtis</h3>
      <p className="text-xs text-ink-400">
        {isCandidate
          ? 'Adds the kushti to this candidate’s invite. They see it the next time they open Akhara.'
          : 'Adds the kushti to this teammate’s path. Progress they already have is never reset.'}
      </p>
      {catalogLoading ? (
        <p className="mt-2 text-sm text-ink-400">Loading kushtis…</p>
      ) : catalog.length === 0 ? (
        <p className="mt-2 text-sm text-ink-400">No published kushtis to show. If you expected some, reload the page.</p>
      ) : options.length === 0 ? (
        <p className="mt-2 text-sm text-ink-400">Nothing left to assign.</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          {options.map((t) => (
            <label
              key={t.id}
              className="flex cursor-pointer select-none items-center gap-1.5 rounded-lg border border-rule px-2 py-1 text-sm hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              <input
                type="checkbox"
                checked={picked.includes(t.id)}
                disabled={busy}
                onChange={(e) => setPicked((prev) => (e.target.checked ? [...prev, t.id] : prev.filter((x) => x !== t.id)))}
              />
              {t.title}
              {t.internal && <span className="text-[11px] text-ink-400">internal</span>}
            </label>
          ))}
        </div>
      )}
      {hiddenInternal > 0 && (
        <p className="mt-2 text-xs text-ink-400">
          {hiddenInternal} internal kushti{hiddenInternal === 1 ? ' is' : 's are'} not offered to candidates. Untick
          Internal on the Kushtis tab to allow one.
        </p>
      )}
      {picked.length > 0 && (
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="mt-2 rounded-lg bg-verdant-700 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Assigning…' : `Assign ${picked.length} kushti${picked.length === 1 ? '' : 's'}`}
        </button>
      )}
      {msg && (
        <p className={`mt-2 text-xs font-medium ${msg.ok ? 'text-verdant-600 dark:text-verdant-300' : 'text-terracotta-700'}`} role="status">
          {msg.text}
        </p>
      )}
    </section>
  );
}

export function PersonView({
  data,
  now,
  onExempt,
  catalog,
  catalogLoading,
  onAssign,
}: {
  data: PersonDetail;
  now?: number;
  onExempt?: (exempt: boolean) => void;
  catalog?: CatalogTrack[];
  catalogLoading?: boolean;
  onAssign?: (trackIds: string[]) => Promise<AssignResult>;
}) {
  const t = now ?? Date.now();
  const { person: p, days, tracks, mittis, recent } = data;
  const last7 = p.week.akharaMin + p.week.mittiMin;
  const all = p.allTime.akharaMin + p.allTime.mittiMin;
  const split = (a: number, m: number) => (
    <>
      <span className={AKHARA_TEXT}>Akhara {formatDuration(a)}</span> ·{' '}
      <span className={MITTI_TEXT}>Mitti {formatDuration(m)}</span>
    </>
  );
  const maxMitti = Math.max(1, ...mittis.map((m) => m.activeMinutes));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">{p.displayName || p.email}</h1>
        <TypeChip type={p.type} />
        <PausedChip paused={p.paused} />
        {p.role && <span className="text-xs text-ink-400">{p.role}</span>}
      </div>
      <p className="text-xs text-ink-400">
        {p.email} · joined {fmtDate(p.joinedAt)} · last active {relativeAgo(p.lastActiveAt, t)}
      </p>

      {p.type === 'candidate' && <AutoPause person={p} inactivityDays={data.inactivityDays} onExempt={onExempt} />}

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Last 7 days" value={formatDuration(last7)} note={split(p.week.akharaMin, p.week.mittiMin)} />
        <Tile label="All time" value={formatDuration(all)} note={split(p.allTime.akharaMin, p.allTime.mittiMin)} />
        <Tile label="Zor" value={p.allTime.zor} note={`${p.zorMonth} this month`} />
        <Tile label="Steps, 7 days" value={p.week.steps} note="Paintras completed" />
      </div>

      <section className="mt-5 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
        <WeeklyChart days={days} />
      </section>

      <section className="mt-5">
        <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">Path</h3>
        {tracks.length === 0 ? (
          <p className="mt-2 text-sm text-ink-400">No trails assigned yet.</p>
        ) : (
          <div className="mt-2 space-y-2">
            {tracks.map((tr) => {
              const pct = tr.totalSteps > 0 ? Math.round((tr.completedSteps / tr.totalSteps) * 100) : 0;
              return (
                <div
                  key={tr.trackId}
                  className="rounded-xl border border-rule bg-paper-0 px-3 py-2 dark:border-clay-rule dark:bg-clay-surface"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm text-ink-800 dark:text-paper-100">{tr.title}</span>
                    <span className="shrink-0 text-xs tabular-nums text-ink-400">
                      {tr.completedSteps} of {tr.totalSteps} · {tr.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-paper-200 dark:bg-clay-rule">
                    <div className="h-1 rounded-full bg-ink-700 dark:bg-paper-200" style={{ width: `${pct}%` }} />
                  </div>
                  {tr.quizzes.length > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {tr.quizzes.map((q) => (
                        <li key={q.stepId} className="flex items-center justify-between text-xs">
                          <span className="text-ink-500 dark:text-paper-300">{q.title || q.stepId}</span>
                          <span className="tabular-nums text-ink-700 dark:text-paper-200">
                            {q.score ?? '—'}%{' '}
                            <span className="text-ink-400">
                              · {q.attempts} attempt{q.attempts === 1 ? '' : 's'}
                              {q.retakeScores.length > 0 ? ` (retakes: ${q.retakeScores.join(', ')})` : ''}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {catalog && onAssign && <AssignKushtis data={data} catalog={catalog} catalogLoading={catalogLoading} onAssign={onAssign} />}

      <section className="mt-5">
        <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">Mittis</h3>
        <p className="text-xs text-ink-400">Practice sites. Time is active minutes on that Mitti.</p>
        {mittis.length === 0 ? (
          <p className="mt-2 text-sm text-ink-400">No Mitti yet.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-xl border border-rule dark:border-clay-rule">
            <table className="w-full text-sm">
              <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                <tr>
                  <th className="px-3 py-2 font-medium">State</th>
                  <th className="px-3 py-2 font-medium" title="Active minutes on this Mitti">Time</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium">Last active</th>
                  <th className="px-3 py-2 font-medium">Expires</th>
                </tr>
              </thead>
              <tbody>
                {mittis.map((m) => (
                  <tr key={m.slug} className="border-t border-rule dark:border-clay-rule">
                    <td className="px-3 py-2">{m.state}</td>
                    <td className="px-3 py-2">
                      <div className="w-24">
                        <TimeBar akhara={0} mitti={m.activeMinutes} max={maxMitti} />
                        <span className="text-xs tabular-nums">{formatDuration(m.activeMinutes)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-ink-500 dark:text-paper-300">{fmtDate(m.createdAt)}</td>
                    <td className="px-3 py-2 text-ink-500 dark:text-paper-300">{relativeAgo(m.lastActiveAt, t)}</td>
                    <td className="px-3 py-2 text-ink-500 dark:text-paper-300">{fmtDate(m.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-5">
        <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">Recent</h3>
        <dl className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ['Last login', relativeAgo(recent.lastLoginTs, t)],
              ['Logins', String(recent.loginCount)],
              ['Pageviews', String(recent.pageviews)],
              ['Pages visited', String(recent.distinctPages)],
            ] as const
          ).map(([l, v]) => (
            <div key={l} className="rounded-lg bg-paper-100 px-3 py-2 dark:bg-clay-raised/40">
              <dt className="text-[11px] uppercase tracking-wide text-ink-400">{l}</dt>
              <dd className="text-sm font-medium text-ink-800 dark:text-paper-100">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-1 text-xs text-ink-400">From the newest 2,000 events.</p>
      </section>
    </div>
  );
}

export default function AdminPerson() {
  const { uid = '' } = useParams();
  // Back to the roster as it was sorted (the roster passes its ?sort=&dir= along).
  const from = (useLocation().state as { from?: unknown } | null)?.from;
  const back = `/admin${typeof from === 'string' && from.startsWith('?') ? from : ''}`;
  const [data, setData] = useState<PersonDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { tracks: published, loading: catalogLoading } = useTracks();

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setErr(null);
    void (async () => {
      const res = await getPerson(uid);
      if (cancelled) return;
      if (res.status === 403) setErr('This view is for Khalifas (admins) only.');
      else if (res.status === 404) setErr('No such person.');
      else if (!res.ok) setErr(res.status === 0 ? 'Network error.' : `Failed (HTTP ${res.status}).`);
      else setData(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const onExempt = (exempt: boolean) => {
    void (async () => {
      const res = await setPersonExempt(uid, exempt);
      if (!res.ok) return setErr(`Could not change the auto-pause (HTTP ${res.status}).`);
      setErr(null);
      setData((d) => (d ? { ...d, person: { ...d.person, inactivityExempt: exempt || undefined } } : d));
    })();
  };

  // One call per kushti. The page reloads either way so the list matches what is now theirs.
  const onAssign = async (trackIds: string[]): Promise<AssignResult> => {
    const out: AssignResult = { error: null, granted: 0, viaDomain: false };
    for (const id of trackIds) {
      const res = await assignTrack(uid, id);
      if (!res.ok) {
        const code = (res.data as { error?: string } | undefined)?.error ?? '';
        out.error = ASSIGN_ERRORS[code] ?? `Could not assign (HTTP ${res.status}).`;
        break;
      }
      if (res.data.created) out.granted += 1;
      if ((res.data as { via?: string }).via === 'domain' && res.data.created) out.viaDomain = true;
    }
    const fresh = await getPerson(uid);
    if (fresh.ok) setData(fresh.data);
    return out;
  };

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-[1600px] px-3 pb-16 pt-4 lg:px-6">
        <Link to={back} className="inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-900 dark:hover:text-paper-50">
          <ArrowLeft className="h-4 w-4" /> People
        </Link>
        <div className="mt-3" />
        {err && <p className="text-sm text-terracotta-700">{err}</p>}
        {!data && !err && <p className="text-sm text-ink-400">Loading…</p>}
        {data && (
          <PersonView
            data={data}
            onExempt={onExempt}
            catalog={published.map((t) => ({ id: t.id, title: t.title, internal: t.internal }))}
            catalogLoading={catalogLoading}
            onAssign={onAssign}
          />
        )}
      </main>
    </div>
  );
}
